const LENS_TYPE_CODE = {
  'Single Vision': '1',
  BiFocal: '2',
  Progressive: '3',
  TriFocal: '4',
}

const LENS_TYPE_LABEL = {
  1: 'Single Vision',
  2: 'BiFocal',
  3: 'Progressive',
  4: 'TriFocal',
}

const FRAME_TYPE_CODE = {
  'Metal Edge': 'METAL',
  'Metal 1/2 Eye': 'MHALF',
  'Zyl Edge': 'ZYL',
  'Zyl 1/2 Eye': 'HALF',
  'Grooved Rimless': 'GROOVE',
  'Drilled Rimless': 'DRILL',
  'Drill 2 Holes': 'DRILL2',
  'Drill 4 Holes': 'DRILL4',
  'Drill 6 Holes': 'DRILL6',
  'Drill 8 Holes': 'DRILL8',
}

const FRAME_TYPE_LABEL = Object.fromEntries(
  Object.entries(FRAME_TYPE_CODE).map(([label, code]) => [code, label]),
)

const THICKNESS_TYPE_CODE = {
  'Lab decides best thickness': 'DRS',
  'Center thickness': 'CTR',
  'Thickness at drilling point': 'DRL',
}

const THICKNESS_TYPE_LABEL = Object.fromEntries(
  Object.entries(THICKNESS_TYPE_CODE).map(([label, code]) => [code, label]),
)

const LENS_PRESCRIPTION_TYPE = {
  Manual: 'manual',
  'Single Vision': 'single vision',
  MultiFocal: 'multifocal',
}

const LENS_PRESCRIPTION_TYPE_LABEL = {
  manual: 'Manual',
  'single vision': 'Single Vision',
  multifocal: 'MultiFocal',
}

const str = (value) => (value === undefined || value === null ? '' : String(value).trim())

const filled = (value) => str(value) !== ''

const mapBase = (value) => {
  const base = str(value)
  if (!base || base === '-') return ''
  return base.toUpperCase()
}

const unmapBase = (value) => {
  const base = str(value)
  if (!base) return ''
  return base.charAt(0) + base.slice(1).toLowerCase()
}

export const isApiLensesShape = (lenses = {}) => (
  !!(lenses.rx || lenses.lab_account || lenses.frame_details || lenses.frame_box || lenses.default_diagnosis)
)

export const isUiLensesShape = (lenses = {}) => (
  !!(lenses.od || lenses.os || lenses.laboratory || lenses.frame_attributes
    || lenses.special_parameters || lenses.prescription_focal
    || LENS_TYPE_CODE[lenses.lens_type] || FRAME_TYPE_CODE[lenses.frame_type]
    || THICKNESS_TYPE_CODE[lenses.thickness_type])
)

const hasLensesInput = (lenses = {}) => (
  filled(lenses?.od?.sphere) || filled(lenses?.os?.sphere)
  || filled(lenses?.lens_type) || filled(lenses?.laboratory?.account)
  || filled(lenses?.frame_type) || filled(lenses?.a_box)
)

/** UI form values → API claim_info.lenses payload */
export function buildEyeMedLensesPayload(lenses = {}) {
  if (!lenses || typeof lenses !== 'object') return {}
  if (isApiLensesShape(lenses) && !isUiLensesShape(lenses)) return lenses
  if (!hasLensesInput(lenses) && !isApiLensesShape(lenses)) return lenses

  const od = lenses.od || {}
  const os = lenses.os || {}
  const attrs = lenses.frame_attributes || {}
  const specialParams = lenses.special_parameters || {}
  const lensTypeCode = LENS_TYPE_CODE[lenses.lens_type] || str(lenses.lens_type)
  const isProgressive = lenses.lens_type === 'Progressive' || lensTypeCode === '3'
  const thicknessCode = THICKNESS_TYPE_CODE[lenses.thickness_type] || str(lenses.thickness_type)
  const showThickness = thicknessCode === 'CTR' || thicknessCode === 'DRL'

  const lab = lenses.laboratory || {}
  const prescriptionFocal = lenses.prescription_focal?.value || lenses.prescription_focal || 'Manual'
  const payload = {
    lab_account: str(lab.account),
    lab_program_code: str(lab.lab_program_code),
    lab_access_groups: str(lab.lab_access_groups),
    lab_txit: str(lab.lab_txit),
    supplier_location_id: str(lab.supplier_location_id),
    ship_to: str(lab.ship_to),
    label: str(lab.label),
    lens_perscription_type: LENS_PRESCRIPTION_TYPE[prescriptionFocal] || 'manual',
    rx: {
      right_sphere: str(od.sphere),
      right_cylinder: str(od.cylinder),
      right_axis: str(od.axis),
      right_add: str(od.add),
      left_sphere: str(os.sphere),
      left_cylinder: str(os.cylinder),
      left_axis: str(os.axis),
      left_add: str(os.add),
    },
    prism: {
      right_prism1: str(od.prism_1),
      right_base1: mapBase(od.base_1),
      right_prism2: str(od.prism_2),
      right_base2: mapBase(od.base_2),
      left_prism1: str(os.prism_1),
      left_base1: mapBase(os.base_1),
      left_prism2: str(os.prism_2),
      left_base2: mapBase(os.base_2),
    },
    right_pd: str(od.distance_pd),
    left_pd: str(os.distance_pd),
    near_right_pd: str(od.near_pd),
    near_left_pd: str(os.near_pd),
    right_height: str(od.height),
    left_height: str(os.height),
    lens_type: lensTypeCode,
    lens_material: str(lenses.lens_material),
    default_diagnosis: str(lenses.frame_diagnosis || 'Z01.00'),
    other_diagnosis: str(lenses.other_diagnosis),
    special_params: { ...specialParams },
    standard_shape: filled(lenses.standard_shape) ? Number(lenses.standard_shape) || str(lenses.standard_shape) : '',
    frame: {
      manufacturer: str(attrs.manufacturer),
      brand: str(attrs.brand),
      model: str(attrs.model),
      color: str(attrs.color),
      eye_size: str(attrs.eye_size),
      temple_length: str(attrs.temple_length),
      sku: str(attrs.sku),
    },
    frame_box: {
      a_box: str(lenses.a_box),
      b_box: str(lenses.b_box),
      dbl: str(lenses.dbl),
      ed: str(lenses.ed),
    },
    frame_details: {
      frame_type: FRAME_TYPE_CODE[lenses.frame_type] || str(lenses.frame_type),
      thickness_type: thicknessCode,
      thickness_right: showThickness ? str(lenses.thickness_re) : '',
      thickness_left: showThickness ? str(lenses.thickness_le) : '',
    },
  }

  if (isProgressive) {
    payload.progressive_manufacturer = str(lenses.progressive_manufacturer)
    payload.progressive_product = str(lenses.progressive_product)
  } else if (filled(lenses.lens_design)) {
    payload.lens_design = str(lenses.lens_design)
  }

  if (lenses.specify_base_curve === 'Yes' && filled(lenses.base_curve)) {
    payload.base_curve = str(lenses.base_curve)
  }

  return payload
}

/** API claim_info.lenses → UI form values */
export function normalizeEyeMedLensesForForm(lenses = {}, availableLabs = []) {
  if (!lenses || typeof lenses !== 'object') return lenses
  if (!isApiLensesShape(lenses) || isUiLensesShape(lenses)) return lenses

  const rx = lenses.rx || {}
  const prism = lenses.prism || {}
  const frame = lenses.frame || {}
  const frameBox = lenses.frame_box || {}
  const frameDetails = lenses.frame_details || {}
  const lensTypeLabel = LENS_TYPE_LABEL[lenses.lens_type] || lenses.lens_type || ''
  const thicknessLabel = THICKNESS_TYPE_LABEL[frameDetails.thickness_type] || frameDetails.thickness_type || ''
  const lab = (availableLabs || []).find((item) => item?.account === lenses.lab_account)
  const hasPrism = [
    prism.right_prism1, prism.right_prism2, prism.left_prism1, prism.left_prism2,
  ].some(filled)

  const prescriptionType = str(lenses.lens_perscription_type).toLowerCase()
  return {
    laboratory: lab || (filled(lenses.lab_account) ? { account: lenses.lab_account } : undefined),
    prescription_focal: {
      value: LENS_PRESCRIPTION_TYPE_LABEL[prescriptionType] || 'Manual',
    },
    with_prism: hasPrism,
    od: {
      sphere: str(rx.right_sphere),
      cylinder: str(rx.right_cylinder),
      axis: str(rx.right_axis),
      add: str(rx.right_add),
      prism_1: str(prism.right_prism1),
      base_1: unmapBase(prism.right_base1),
      prism_2: str(prism.right_prism2),
      base_2: unmapBase(prism.right_base2),
      distance_pd: str(lenses.right_pd),
      near_pd: str(lenses.near_right_pd),
      height: str(lenses.right_height),
    },
    os: {
      sphere: str(rx.left_sphere),
      cylinder: str(rx.left_cylinder),
      axis: str(rx.left_axis),
      add: str(rx.left_add),
      prism_1: str(prism.left_prism1),
      base_1: unmapBase(prism.left_base1),
      prism_2: str(prism.left_prism2),
      base_2: unmapBase(prism.left_base2),
      distance_pd: str(lenses.left_pd),
      near_pd: str(lenses.near_left_pd),
      height: str(lenses.left_height),
    },
    lens_type: lensTypeLabel,
    lens_design: str(lenses.lens_design),
    progressive_manufacturer: str(lenses.progressive_manufacturer),
    progressive_product: str(lenses.progressive_product),
    lens_material: str(lenses.lens_material),
    frame_diagnosis: str(lenses.default_diagnosis || 'Z01.00'),
    other_diagnosis: str(lenses.other_diagnosis),
    special_parameters: { ...(lenses.special_params || {}) },
    specify_base_curve: filled(lenses.base_curve) ? 'Yes' : 'No',
    base_curve: str(lenses.base_curve),
    standard_shape: filled(lenses.standard_shape) ? String(lenses.standard_shape) : '',
    frame_attributes: {
      manufacturer: str(frame.manufacturer),
      brand: str(frame.brand),
      model: str(frame.model),
      color: str(frame.color),
      eye_size: str(frame.eye_size),
      temple_length: str(frame.temple_length),
      sku: str(frame.sku),
    },
    a_box: str(frameBox.a_box),
    b_box: str(frameBox.b_box),
    dbl: str(frameBox.dbl),
    ed: str(frameBox.ed),
    frame_type: FRAME_TYPE_LABEL[frameDetails.frame_type] || frameDetails.frame_type || '',
    thickness_type: thicknessLabel,
    thickness_re: str(frameDetails.thickness_right),
    thickness_le: str(frameDetails.thickness_left),
  }
}
